import React, { useState } from 'react';
import { useApp } from '../../AppContext';
import { ALL_HOURS, DateOverrideItem, formatDate, todayIso } from '../../types';

export default function OverridesEditor() {
  const { dateOverrides, upsertDateOverride, deleteDateOverride } = useApp();
  const [editing, setEditing] = useState<DateOverrideItem | 'new' | null>(null);

  return (
    <div className="bg-forest p-6 rounded-3xl border border-sage/15 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-serif text-xl">Eccezioni per data</h3>
          <p className="text-xs text-sage mt-1">Sovrascrivi un singolo giorno: festività, chiusura straordinaria o orari custom.</p>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="bg-sage/20 hover:bg-sage/30 text-cream text-xs uppercase tracking-wider font-bold px-3 py-2 rounded-lg smooth-transition"
        >
          + Aggiungi
        </button>
      </div>

      {dateOverrides.length === 0 && (
        <p className="text-xs text-sage italic text-center py-4">Nessuna eccezione configurata.</p>
      )}

      <div className="space-y-2">
        {dateOverrides.map(o => (
          <div key={o.id} className="bg-forestDark/40 rounded-xl border border-sage/15 p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-bold text-sm text-cream">{formatDate(o.date)}</div>
              <div className="text-[11px] text-sage truncate">
                {o.is_open
                  ? (o.hours.length > 0 ? `Aperto custom: ${o.hours.join(', ')}` : 'Aperto senza ore')
                  : 'CHIUSO'}
                {o.note && ` — ${o.note}`}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(o)} className="text-xs text-sage hover:text-cream px-2 py-1">Modifica</button>
              <button
                onClick={() => { if (window.confirm(`Eliminare l'eccezione del ${formatDate(o.date)}?`)) deleteDateOverride(o.id); }}
                className="text-xs text-rose-400 hover:text-rose-300 px-2 py-1"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <OverrideForm
          initial={editing === 'new' ? null : editing}
          onSave={async data => { await upsertDateOverride(data); setEditing(null); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface FormPayload {
  id?: number;
  date: string;
  is_open: boolean;
  hours: string[];
  note: string;
}

function OverrideForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: DateOverrideItem | null;
  onSave: (data: FormPayload) => Promise<void>;
  onCancel: () => void;
}) {
  const [date, setDate] = useState(initial?.date || todayIso());
  const [isOpen, setIsOpen] = useState<boolean>(initial?.is_open ?? false);
  const [hours, setHours] = useState<string[]>(initial?.hours || []);
  const [note, setNote] = useState(initial?.note || '');
  const [saving, setSaving] = useState(false);

  const toggleHour = (h: string) => {
    setHours(prev => prev.includes(h) ? prev.filter(x => x !== h) : [...prev, h].sort());
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        id: initial?.id,
        date, is_open: isOpen,
        hours: isOpen ? hours : [],
        note,
      });
    } catch { /* AppContext already shows toast */ }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 bg-forestDark/80 backdrop-blur-sm">
      <div className="bg-cream text-charcoal rounded-3xl shadow-2xl max-w-lg w-full p-8 space-y-5 max-h-[90vh] overflow-y-auto">
        <div>
          <h4 className="font-serif text-xl text-forestDark">{initial ? 'Modifica eccezione' : 'Nuova eccezione'}</h4>
          <p className="text-xs text-charcoal/60 mt-1">Vince sul default settimanale per quella data.</p>
        </div>

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs font-semibold text-charcoal/70">Data</label>
            <input
              type="date"
              value={date}
              min={todayIso()}
              onChange={e => setDate(e.target.value)}
              disabled={!!initial}
              className="w-full bg-beigeBG border border-forest/15 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-forest disabled:opacity-60"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={isOpen} onChange={e => setIsOpen(e.target.checked)} className="text-forest focus:ring-forest" />
            <span className="text-sm font-semibold text-forestDark">{isOpen ? 'Aperto con orari custom' : 'CHIUSO per questa data'}</span>
          </label>

          {isOpen && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-charcoal/70">Fasce orarie disponibili</label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5">
                {ALL_HOURS.map(h => {
                  const active = hours.includes(h);
                  return (
                    <button
                      key={h}
                      onClick={() => toggleHour(h)}
                      className={`p-1.5 rounded text-[11px] font-semibold border transition-all ${
                        active ? 'bg-forest text-cream border-forest' : 'bg-beigeBG text-forestDark/70 border-forest/15 hover:border-forest/40'
                      }`}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs font-semibold text-charcoal/70">Nota (facoltativa)</label>
            <input
              type="text"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Es. Vacanza, Festa nazionale, Orario serale extra…"
              className="w-full bg-beigeBG border border-forest/15 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-forest"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-forest/10">
          <button onClick={onCancel} className="text-xs uppercase tracking-widest font-bold text-sage hover:text-forest">Annulla</button>
          <button
            onClick={save}
            disabled={saving}
            className="btn-shine bg-forest hover:bg-forestDark text-cream px-6 py-2.5 rounded-full text-sm font-semibold smooth-transition disabled:opacity-60"
          >
            {saving ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}
